<template>
  <div>
    <div :style="cssVars" class="fill-height bg"></div>
    <v-tooltip left>
      <template #activator="{ on, attrs }">
        <v-btn
          class="mb-10 mr-8"
          fab
          small
          absolute
          bottom
          right
          v-bind="attrs"
          v-on="on"
          @click="scrollTop()"
        >
          <v-icon>mdi-arrow-up</v-icon>
        </v-btn>
      </template>
      <span>{{ $t('common.Return') }}</span>
    </v-tooltip>

    <ParticipantFilters
      class="pa-5"
      :only-speakers="onlySpeakers"
    ></ParticipantFilters>

    <div class="overflow-y-auto scroller px-5 my-0 py-0">
      <div id="top"></div>
      <div class="d-flex flex-wrap justify-space-around">
        <platform-user-card
          v-for="p in platformUsers"
          :key="p.userId"
          :pu="p"
          class="mb-10 pa-0"
        />
        <!-- Hack to get the last row of cols inline -->
        <platform-user-card v-for="x in 10" :key="x" class="mb-10 pa-0" />
        <div v-intersect.quiet="intersected" class="searchlp" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import PlatformUserCard from '@/components/PlatformUserCard.vue';
import ParticipantFilters from '@/components/ParticipantFilters.vue';
import Observer from '@/components/Observer.vue';
import { PlatformGetters } from '@/store/platform/types';
import {
  PlatformUserActions,
  PlatformUserGetters,
  PlatformUserItem,
} from '@/store/platformUser/types';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { AppInsightsLogger } from '../services/appInsightsLogger';
const platformUserNamespace = 'platformUser';
const platformNamespace = 'platform';

@Component({
  components: {
    ParticipantFilters,
    PlatformUserCard,
    Observer,
  },
})
export default class Participants extends Vue {
  /* PUBLIC PROPERTIES */
  @Prop()
  public onlySpeakers!: boolean;

  /* PRIVATE PROPERTIES */
  private loading = false;
  private PAGINATION_LENGTH = 25;

  /* VUEX GETTERS */
  @Getter(PlatformUserGetters.GET_CURRENT_PAGE, {
    namespace: platformUserNamespace,
  })
  private currentPage!: number;

  @Getter(PlatformUserGetters.PLATFORM_USERS, {
    namespace: platformUserNamespace,
  })
  private platformUsers!: PlatformUserItem[];

  @Getter(PlatformGetters.PORTAL_BACKGROUND_URI, {
    namespace: platformNamespace,
  })
  private portalBackgroundUri!: string;

  @Getter(PlatformUserGetters.TOTAL_PLATFORM_USER_COUNT, {
    namespace: platformUserNamespace,
  })
  private totalPlatformUserCount!: number;

  /* VUEX ACTIONS */
  @Action(PlatformUserActions.SET_CURRENT_PAGE, {
    namespace: platformUserNamespace,
  })
  private setCurrentPage!: (page: number) => Promise<void>;

  @Action(PlatformUserActions.SET_PLATFORM_USER_DATA, {
    namespace: platformUserNamespace,
  })
  private setUserPlatformData!: (
    data: { items: PlatformUserItem[]; totaItemsCount: number } | undefined,
  ) => void;

  @Action(PlatformUserActions.SET_PLATFORM_USERS_LOADING, {
    namespace: platformUserNamespace,
  })
  private setPlatformUsersLoading!: (loading: boolean) => void;

  /* WATCHES */
  @Watch('$route.query', { immediate: false, deep: true })
  private async onQueryChange() {
    this.setPlatformUsersLoading(true);
    this.setCurrentPage(1);
    this.clearPlatformUsers();
    await this.loadData();
    this.setPlatformUsersLoading(false);
  }

  /* LOCAL GETTERS/SETTERS */
  get cssVars(): unknown {
    return {
      '--bg-image': `url('${this.portalBackgroundUri}')`,
    };
  }
  get paginationLength(): number {
    return Math.ceil(this.totalPlatformUserCount / 12);
  }

  get cardWidth(): number {
    if (this.$vuetify.breakpoint.mdAndDown) {
      return 320;
    } else {
      return 400;
    }
  }

  /* PRIVATE METHODS*/
  private async beforeMount() {
    this.setPlatformUsersLoading(true);
    this.clearPlatformUsers();
    this.setCurrentPage(1);
    await this.loadData();
    this.setPlatformUsersLoading(false);
  }

  private clearPlatformUsers() {
    this.setUserPlatformData(undefined);
  }

  private async intersected() {
    if (
      !this.loading &&
      this.currentPage * this.PAGINATION_LENGTH < this.totalPlatformUserCount
    ) {
      this.setCurrentPage(this.currentPage + 1);
      await this.loadData();
    }
  }

  private async loadData(): Promise<void> {
    this.loading = true;
    const platformId = sessionStorage.getItem('platformId') ?? '';

    let queryString = '?page=' + this.currentPage;
    if (this.onlySpeakers) {
      queryString = queryString + '&speaker=true';
    }

    if (window.location.search) {
      queryString = queryString + '&' + window.location.search.substring(1); // remove ?
    }

    try {
      const res = await Vue.$http.get(
        `/api/platform/${platformId}/users${queryString}`,
      );
      this.setUserPlatformData(res.data);
    } catch (e: any) {
      AppInsightsLogger.logError(
        'Participants - loadData failed',
        undefined,
        true,
      );
      AppInsightsLogger.logException(e, false);
    }
    this.loading = false;
  }

  private scrollTop() {
    const e = document.getElementById('top');
    if (e) {
      e.scrollIntoView();
    }
  }
}
</script>

<style scoped>
.bg {
  width: 100%;
  background-image: var(--bg-image);
  background-size: cover;
  position: fixed;
  z-index: -1;
}
.bg::before {
  content: '';
  background-color: rgb(249, 249, 249);
  opacity: 0.7;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  position: absolute;
}

.scroller {
  height: calc(100vh - 245px);
  width: calc(100% - 20px);
  font-size: 12px;
  overflow-x: hidden;
  z-index: 9999;
}

.scroller::-webkit-scrollbar {
  width: 10px;
}

.scroller::-webkit-scrollbar-track {
  background: #b0b0b050;
  border-left: 0;
}

.scroller::-webkit-scrollbar-thumb {
  background: #b0b0b090;
  border: 0;
  border-radius: 7px;
}

.scroller::-webkit-scrollbar-thumb:hover {
  background: #00000030;
}

.scroller::-webkit-scrollbar-thumb:active {
  background: #00000090;
}
</style>
